/**
 * Line-item upsells (easel and friends): products sold next to a frame as their own cart line.
 * Config lives on the Shopify product (`cart-upsell` tag, Frame Dimension Limits metaobject on
 * the variant) and is served by /api/v1/shopify_products_by_tag/cart-upsell, so web, POS and
 * iOS evaluate the same rule. The order import links the child to the frame by `_parent_design`.
 */
export const CART_UPSELL_TAG = "cart-upsell";
export const UPSELL_DIGITAL_ONLY = false;

const UPSELL_FRAME_PRODUCT_TYPES = ["Custom Frame", "Curated Design Frame", "Frame Bundle"];

export function frameFitsLimits (limits, finalFrameSize) {
  if (!limits || !finalFrameSize?.width || !finalFrameSize?.height) {
    return false;
  }

  const fits = (frameWidth, frameHeight) => (
    limits.minimumFrameWidth <= frameWidth
      && limits.minimumFrameHeight <= frameHeight
      && limits.maximumFrameWidth >= frameWidth
      && limits.maximumFrameHeight >= frameHeight
  );

  return fits(finalFrameSize.width, finalFrameSize.height)
    || fits(finalFrameSize.height, finalFrameSize.width);
}

export function isCartUpsellProduct (product) {
  return Boolean(product?.tags?.includes(CART_UPSELL_TAG));
}

export function upsellVariant (product) {
  const variants = product?.variants || [];

  return variants.find((variant) => variant.frameDimensionLimits) || variants[0];
}

export function upsellPriceCents (product) {
  const price = parseFloat(upsellVariant(product)?.price);

  return Number.isNaN(price) ? 0 : Math.round(price * 100);
}

export function frameAllowsUpsells ({ productType, isTableTop = false, isSingleFrame = true, isDigital = true } = {}) {
  if (!UPSELL_FRAME_PRODUCT_TYPES.includes(productType) || isTableTop || !isSingleFrame) {
    return false;
  }

  return !UPSELL_DIGITAL_ONLY || isDigital;
}

export function eligibleUpsellProducts ({ products = [], finalFrameSize, ...frame } = {}) {
  if (!frameAllowsUpsells(frame)) {
    return [];
  }

  return products.filter((product) => {
    if (!isCartUpsellProduct(product) || product.status !== "ACTIVE") {
      return false;
    }

    const variant = upsellVariant(product);

    return Boolean(variant?.purchasable) && frameFitsLimits(variant.frameDimensionLimits, finalFrameSize);
  });
}

export function isChildLineOf (line, parentProperties) {
  const properties = line?.properties || {};
  const { _frame_spec_number: frameSpecNumber, _bundle_spec_number: bundleSpecNumber } = parentProperties || {};

  return Boolean(
    (frameSpecNumber && properties._parent_design === frameSpecNumber)
      || (bundleSpecNumber && (properties._parent_bundle_spec === bundleSpecNumber || properties._parent_design === bundleSpecNumber))
  );
}

export function upsellLineItems ({ products = [], parentDesignNumber, parentBundleSpecNumber } = {}) {
  const variantIdList = products.map((product) => String(upsellVariant(product).shopifyVariantId)).join(",");

  return products.map((product) => ({
    id: String(upsellVariant(product).shopifyVariantId),
    quantity: 1,
    properties: {
      _parent_design: parentDesignNumber,
      ...(parentBundleSpecNumber ? { _parent_bundle_spec: parentBundleSpecNumber } : {}),
      _upsell_item: "true",
      _upsell_item_variant_id_list: variantIdList
    }
  }));
}

export function upsellParentProperties (products = []) {
  if (!products.length) {
    return {};
  }

  const totalCents = products.reduce((sum, product) => sum + upsellPriceCents(product), 0);

  return {
    _upsell_parent: "true",
    _upsell_price: String(totalCents),
    _upsell_item_variant_id_list: products.map((product) => String(upsellVariant(product).shopifyVariantId)).join(",")
  };
}
